import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  QaiTableComponent,
  TableRow,
  QaiLoadingComponent,
  QaiBadgeKind,
  KebabMenuItem,
  SortDirection,
  QaiSearchableSelectOption,
  QaiSearchableSelectComponent,
  HeaderConfig,
  QaiFieldComponent,
  QaiInputComponent,
  QaiIconComponent,
  QaiSlotDirective,
} from '@qai/ui';
import { IAssignment } from 'src/app/interfaces/self-serve/assignment-manager/iassignment';
import { AssignmentsService } from 'src/app/services/self-serve/assignments.service';
import { ActivatedRoute, Router } from '@angular/router';
import { formatLongMonthDate } from 'src/app/utils/date-parser.util';
import { PaginationControllerService } from 'src/app/services/self-serve/pagination-controller.service';
import { AssignmentStatusValueEnum, AssignmentStatusLabelEnum} from 'src/app/utils/enums/self-serve/assignment-type.enum';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';
import { transformLabel } from 'src/app/utils/string-replacements';
import { filterByName } from 'src/app/utils/search.util';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { localeLowerCase } from '../../../../../../utils/locale-case.util';
import { take } from 'rxjs';
import { LocaleService } from 'src/app/services/locale.service';

export interface AssignmentStatusChange {
  uuid: string;
  targetStatus: string;
}

const SIMULATION_TABLE_HEADERS: HeaderConfig[] = [
  { name: 'Name',               sortable: true, columnWidth: '19%' },
  { name: 'Assignment start date', sortable: true, columnWidth: '15%' },
  { name: 'Assignment end date',   sortable: true, columnWidth: '15%' },
  { name: 'Learner assigned',   sortable: true, columnWidth: '16%' },
  { name: 'Assignment type',    sortable: true, columnWidth: '16%' },
  { name: 'Assignment status',  sortable: true, columnWidth: '13%' },
];

const ASSIGNMENT_COL_KEYS = [
  'ASSIGNMENTS_COL_NAME',
  'ASSIGNMENTS_COL_START_DATE',
  'ASSIGNMENTS_COL_END_DATE',
  'ASSIGNMENTS_COL_LEARNERS',
  'ASSIGNMENTS_COL_TYPE',
  'ASSIGNMENTS_COL_STATUS',
] as const;

@Component({
  selector: 'app-assignments',
  standalone: true,
  templateUrl: './assignments.component.html',
  styleUrls: ['./assignments.component.scss'],
  imports: [CommonModule, QaiTableComponent, QaiLoadingComponent, QaiSearchableSelectComponent, QaiFieldComponent, QaiInputComponent, QaiIconComponent, QaiSlotDirective, TranslocoModule],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class AssignmentsComponent implements OnInit {
  tableData: TableRow[] = [];
  statusOptions: QaiSearchableSelectOption[] = [];
  
  // Filter shows empty initially
  selectedStatusValues: string[] = [];

  // Default filter (all except Archived) - used for table display
  defaultValues: string[] = [
    AssignmentStatusValueEnum.DRAFT,
    AssignmentStatusValueEnum.STAGED,
    AssignmentStatusValueEnum.ACTIVE,
    AssignmentStatusValueEnum.COMPLETE,
    AssignmentStatusValueEnum.CLOSED,
  ];
 
  allStatusValues: string[] = [
    AssignmentStatusValueEnum.DRAFT,
    AssignmentStatusValueEnum.STAGED,
    AssignmentStatusValueEnum.ACTIVE,
    AssignmentStatusValueEnum.COMPLETE,
    AssignmentStatusValueEnum.CLOSED,
    AssignmentStatusValueEnum.ARCHIVED,
  ];

  searchName = signal('');

  allAssignments: IAssignment[] = [];
  clearOption = { enable: true, label: 'Clear' };
  visibleOptionsSelectionMode = {
    enable: true,
    selectAllOption: { value: 'selectAll', label: 'Select all' },
    deselectAllOption: { value: 'deselectAll', label: 'Deselect all' },
  };

  othersTextFormatter = (count: number): string =>
    count === 1
      ? this.translocoService.translate('simulationBuilder.OTHERS_SINGULAR')
      : this.translocoService.translate('simulationBuilder.OTHERS_PLURAL', { count });

  headers: HeaderConfig[] = SIMULATION_TABLE_HEADERS.map(h => ({ ...h }));
  isLoading = true;
  initialSortColumn: string = SIMULATION_TABLE_HEADERS[2].name;
  initialSortDirection: SortDirection = 'desc';
  options: { label: string; value: string }[] = [];
  @Input() selectedAccount: { label: string; value: string } | undefined;
  @Input() selectedAccountUuid: string = '';

  @Output() deleteAssignment = new EventEmitter<any>();
  @Output() duplicateAssignment = new EventEmitter<{
    id: string;
    name: string;
  }>();
  @Output() changeAssignmentStatus = new EventEmitter<AssignmentStatusChange>();

  currentPage: number = 1;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private assignmentsService: AssignmentsService,
    private paginationControllerService: PaginationControllerService,
    private amplitudeService: AmplitudeService,
    private translocoService: TranslocoService,
    private localeService: LocaleService
  ) {}

  ngOnInit(): void {
    this.initializePagination();
    this.translocoService.selectTranslation('simulationBuilder').pipe(take(1)).subscribe(() => {
      this.statusOptions = [
        { label: this.translocoService.translate('simulationBuilder.STATUS_DRAFT'), value: AssignmentStatusValueEnum.DRAFT },
        { label: this.translocoService.translate('simulationBuilder.STATUS_STAGED'), value: AssignmentStatusValueEnum.STAGED },
        { label: this.translocoService.translate('simulationBuilder.STATUS_ACTIVE'), value: AssignmentStatusValueEnum.ACTIVE },
        { label: this.translocoService.translate('simulationBuilder.STATUS_ENDED'), value: AssignmentStatusValueEnum.COMPLETE },
        { label: this.translocoService.translate('simulationBuilder.STATUS_CLOSED'), value: AssignmentStatusValueEnum.CLOSED },
        { label: this.translocoService.translate('simulationBuilder.STATUS_ARCHIVED'), value: AssignmentStatusValueEnum.ARCHIVED },
      ];
      this.clearOption = { enable: true, label: this.translocoService.translate('simulationBuilder.BUTTON_CLEAR') };
      this.visibleOptionsSelectionMode = {
        enable: true,
        selectAllOption: { value: 'selectAll', label: this.translocoService.translate('simulationBuilder.SELECT_ALL') },
        deselectAllOption: { value: 'deselectAll', label: this.translocoService.translate('simulationBuilder.DESELECT_ALL') },
      };
      this.headers = SIMULATION_TABLE_HEADERS.map((h, i) => ({
        ...h,
        name: this.translocoService.translate(`simulationBuilder.${ASSIGNMENT_COL_KEYS[i]}`),
      }));
      this.initialSortColumn = this.headers[2].name;
      if (this.allAssignments.length > 0) {
        this.applyFilters(false);
      }
    });
  }

  private initializePagination(): void {
    this.currentPage = 1;
    this.paginationControllerService.setAssignmentsCurrentPage(1);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedAccount']) {
      this.loadAssignments();
    }
    const savedFilters = SelfServeLocalStorageUtil.getSelectedStatusFilters([]);
    if (savedFilters.length > 0) {
      this.selectedStatusValues = savedFilters;
    }
  }
  
  onStatusFilterChange(selectedStatuses: string | string[]): void {
    const incomingStatuses = Array.isArray(selectedStatuses)
      ? selectedStatuses
      : [selectedStatuses];

    // SAFE: values from AssignmentStatusValueEnum, not free text
    this.selectedStatusValues = incomingStatuses.map(s => s.toLowerCase());
    SelfServeLocalStorageUtil.setSelectedStatusFilters(this.selectedStatusValues);
    this.applyFilters();
  }

  onSearchNameChange(event: Event): void {
    this.searchName.set((event.target as HTMLInputElement).value);
    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.SIMBUILDER_SEARCH_ASSIGNMENT);
    this.applyFilters();
  }

  private applyFilters(resetPage = true): void {
    const statusesToFilter = this.selectedStatusValues.length === 0
      // SAFE: fixed enum constants
      ? this.defaultValues.map(s => s.toLowerCase())
      : this.selectedStatusValues;

    const locale = this.translocoService.getActiveLang() || 'en';
    const search = localeLowerCase(this.searchName(), locale).trim();

    const filteredData = filterByName(
      // SAFE: status enum comparison
      this.allAssignments.filter(a => statusesToFilter.includes((a.status || 'staged').toLowerCase())),
      search,
      a => a.name || '',
      locale
    );

    if (resetPage) {
      this.currentPage = 1;
      this.paginationControllerService.setAssignmentsCurrentPage(1);
    }
    this.tableData = this.transformApiDataToTableRows(filteredData);
  }

  public loadAssignments() {
    this.isLoading = true;

    const accountUuid = this.selectedAccount?.value || this.selectedAccountUuid;
    if (!accountUuid) {
      console.error('No valid account UUID found to load assignments.');
      this.isLoading = false;
      return;
    }

    this.assignmentsService.findAllAssignments(accountUuid).subscribe({
      next: (apiData: IAssignment[]) => {
        this.allAssignments = apiData;
        const savedFilters = SelfServeLocalStorageUtil.getSelectedStatusFilters([]);
        if (savedFilters.length > 0) {
          this.selectedStatusValues = savedFilters;
        }
        this.applyFilters(false);
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Failed to load assignments:', err);
        this.isLoading = false;
      },
    });
  }

  onPageChanged(event: { page: number }): void {
    this.currentPage = event.page;
    
    this.paginationControllerService.setAssignmentsCurrentPage(this.currentPage);
  }

  private transformApiDataToTableRows(apiData: IAssignment[]): TableRow[] {
    return apiData.map((assignment) => {
      const kebabOptions = this.getKebabOptions(assignment.status);
  
      return this.createAssignmentTableRow(
        assignment.uuid,
        assignment.name,
        formatLongMonthDate(assignment.startDate, this.localeService.getCurrentLocaleId()),
        formatLongMonthDate(assignment.endDate, this.localeService.getCurrentLocaleId()),
        this.formatUserCount(Number(assignment.userCount)),
        transformLabel(assignment.moduleAssignmentType || ''),
        this.getStatusBadge(assignment.status),
        kebabOptions
      );
    });
  }

  private getKebabOptions(status: string): KebabMenuItem[] {
    // SAFE: status enum switch
    switch ((status || '').toLowerCase()) {
      case AssignmentStatusValueEnum.STAGED:
        return [
          { type: 'click', text: 'Close' },
          { type: 'click', text: 'Archive' },
          { type: 'click', text: 'Duplicate' },
          { type: 'click', text: 'Delete' },
        ];
      case AssignmentStatusValueEnum.ACTIVE:
      case AssignmentStatusValueEnum.COMPLETE:
        return [
          { type: 'click', text: 'Close' },
          { type: 'click', text: 'Archive' },
          { type: 'click', text: 'Duplicate' },
          { type: 'click', text: 'Delete' },
        ];
      case AssignmentStatusValueEnum.CLOSED:
        return [
          { type: 'click', text: 'Reopen' },
          { type: 'click', text: 'Archive' },
          { type: 'click', text: 'Duplicate' },
          { type: 'click', text: 'Delete' },
        ];
      case AssignmentStatusValueEnum.ARCHIVED:
        return [
          { type: 'click', text: 'Reopen' },
          { type: 'click', text: 'Duplicate' },
          { type: 'click', text: 'Delete' },
        ];
      default:
        return [
          { type: 'click', text: 'Delete' },
          { type: 'click', text: 'Duplicate' },
        ];
    }
  }

  onTableRowAction(event: {
    action: KebabMenuItem;
    rowId: string | number;
    rowData: TableRow;
    event: Event;
  }) {
    if (!event.action.text) return;
    // SAFE: hardcoded action-text literals defined in this file
    switch (event.action.text.toLowerCase()) {
      case 'delete':
        this.deleteAssignment.emit({ id: event.rowId, rowData: event.rowData });
        break;
      case 'duplicate':
        const nameCell = event.rowData[this.headers[0].name];
        const name = (nameCell as { value: string }).value;
        this.duplicateAssignment.emit({ id: event.rowId as string, name });
        break;
      case 'close':
        this.changeAssignmentStatus.emit({ uuid: event.rowId as string, targetStatus: 'CLOSED' });
        break;
      case 'archive':
        this.changeAssignmentStatus.emit({ uuid: event.rowId as string, targetStatus: 'ARCHIVED' });
        break;
      case 'reopen':
        this.changeAssignmentStatus.emit({ uuid: event.rowId as string, targetStatus: 'REOPEN' });
        break;
      default:
        break;
    }
  }

  onTableRowClick(event: {
    rowId: string | number;
    rowData: TableRow;
    event: Event;
  }) {
    this.handleEdit(event.rowId);
  }

  private handleEdit(rowId: string | number) {
    this.router.navigate(['assignments', rowId], {
      relativeTo: this.route,
    });
  }

  createAssignmentTableRow(
    id: string | number,
    name: string,
    assignmentStartDate: string,
    assignmentEndDate: string,
    userCount: string,
    assignmentType: string,
    assignmentStatus: { kind: QaiBadgeKind; label: string },
    options: KebabMenuItem[],
  ): TableRow {
    const row = {
      id,
      [this.headers[0].name]: { type: 'text', value: name },
      [this.headers[1].name]: { type: 'text', value: assignmentStartDate },
      [this.headers[2].name]: { type: 'text', value: assignmentEndDate },
      [this.headers[3].name]: { type: 'text', value: userCount },
      [this.headers[4].name]: {
        type: 'badge',
        shape: 'rectangle',
        kind: 'gray',
        label: assignmentType,
      },
      [this.headers[5].name]: {
        type: 'badge',
        shape: 'round',
        kind: assignmentStatus.kind,
        label: assignmentStatus.label,
      },
    };
    return { ...row, options } as unknown as TableRow;
  }

  private getStatusBadge(status: string | null): {
    kind: QaiBadgeKind;
    label: string;
  } {
    if (!status) {
      return { kind: 'yellow', label: this.translocoService.translate('simulationBuilder.STATUS_STAGED') };
    }

    // SAFE: status enum switch
    switch (status.toLowerCase()) {
      case 'staged':
        return { kind: 'yellow', label: this.translocoService.translate('simulationBuilder.STATUS_STAGED') };
      case 'active':
        return { kind: 'green', label: this.translocoService.translate('simulationBuilder.STATUS_ACTIVE') };
      case 'complete':
        return { kind: 'gray-blue', label: this.translocoService.translate('simulationBuilder.STATUS_ENDED') };
      case 'closed':
        return { kind: 'gray-blue-outline', label: this.translocoService.translate('simulationBuilder.STATUS_CLOSED') };
      case 'draft':
        return { kind: 'yellow', label: this.translocoService.translate('simulationBuilder.STATUS_DRAFT') };
      default:
        return { kind: 'yellow', label: transformLabel(status) };
    }
  }

  private formatUserCount(userCount: number): string {
    if (!userCount || userCount === 0) {
      return this.translocoService.translate('simulationBuilder.LEARNER_COUNT_NONE');
    }
    return userCount === 1
      ? this.translocoService.translate('simulationBuilder.LEARNER_COUNT_SINGULAR', { count: userCount })
      : this.translocoService.translate('simulationBuilder.LEARNER_COUNT_PLURAL', { count: userCount });
  }

  get hasNoAssignments(): boolean {
    return this.allAssignments.length === 0;
  }
}